import React, { useRef } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useSuperState } from 'hooks';

import 'components/GMetrixUpload/gmetrix-upload.scss';

type GMetrixUploadRestrictions = {
    allowedExtensions?: string[];
    maxFileSize?: number;
};

type GMetrixUploadBeforeUploadEvent = {
    additionalData: Record<string, any>;
};

type GMetrixUploadStatusChangeEvent = {
    response: {
        response: any;
        status: number;
    };
};

type GMetrixUploadProps = {
    batch?: boolean;
    defaultFiles?: any[];
    multiple?: boolean;
    onBeforeUpload?: (event: GMetrixUploadBeforeUploadEvent) => void;
    onStatusChange?: (event: GMetrixUploadStatusChangeEvent) => void;
    restrictions?: GMetrixUploadRestrictions;
    saveUrl: string;
    withCredentials?: boolean;
};

export const GMetrixUpload = (props: GMetrixUploadProps) => {

    const { formatMessage } = useIntl();

    const {
        multiple = false,
        onBeforeUpload,
        onStatusChange,
        restrictions = {},
        saveUrl,
        withCredentials = false
    } = props;

    const { allowedExtensions = [], maxFileSize } = restrictions;

    const inputRef = useRef<HTMLInputElement>(null);

    const [state, setState] = useSuperState({
        error: '',
        fileName: '',
        status: '' as '' | 'uploading' | 'uploaded' | 'failed'
    });

    const validate = (file: File): string => {

        if (allowedExtensions.length > 0) {

            const ext = '.' + file.name.split('.').pop()?.toLowerCase();
            if (!allowedExtensions.includes(ext)) {

                return `File type not allowed. Allowed: ${allowedExtensions.join(', ')}`;

            }

        }

        if (maxFileSize && file.size > maxFileSize) {

            return `File exceeds maximum size of ${(maxFileSize / 1024 / 1024).toFixed(0)} MB`;

        }

        return '';

    };

    const upload = async (file: File) => {

        setState({ error: '', fileName: file.name, status: 'uploading' });

        const additionalData: Record<string, any> = {};
        if (onBeforeUpload) {

            onBeforeUpload({ additionalData });

        }

        const formData = new FormData();
        formData.append('files', file, file.name);
        Object.keys(additionalData).forEach(key => formData.append(key, additionalData[key]));

        try {

            const response = await fetch(saveUrl, {
                body: formData,
                credentials: withCredentials ? 'include' : 'same-origin',
                method: 'POST'
            });

            const responseBody = await response.json().catch(() => null);

            setState({ status: response.ok ? 'uploaded' : 'failed' });

            if (onStatusChange) {

                onStatusChange({ response: { response: responseBody, status: response.status } });

            }

        } catch {

            setState({ error: 'Upload failed. Please try again.', status: 'failed' });

            if (onStatusChange) {

                onStatusChange({ response: { response: null, status: 0 } });

            }

        }

    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        const file = e.target.files?.[0];
        if (!file) return;

        const validationError = validate(file);
        if (validationError) {

            setState({ error: validationError, fileName: file.name, status: 'failed' });
            return;

        }

        upload(file);

        // Reset input so the same file can be re-selected after clearing
        e.target.value = '';

    };

    const handleClear = () => {

        setState({ error: '', fileName: '', status: '' });

    };

    const statusIcon = state.status === 'uploading'
        ? <i className="fa-solid fa-spinner fa-spin text-primary ms-2" />
        : state.status === 'uploaded'
            ? <i className="fa-solid fa-check text-success ms-2" />
            : state.status === 'failed'
                ? <i className="fa-solid fa-xmark text-danger ms-2" />
                : null;

    return (
        <div className="gmetrix-upload">
            <div className="gmetrix-upload__dropzone">
                <button
                    className="gmetrix-upload__select-btn"
                    disabled={state.status === 'uploading'}
                    onClick={() => inputRef.current?.click()}
                    type="button"
                >
                    <i className="fa-solid fa-upload me-2" />
                    <FormattedMessage defaultMessage="Select files..." id="select_files.28c4f08d" />
                </button>
                <input
                    accept={allowedExtensions.join(',')}
                    className="d-none"
                    multiple={multiple}
                    onChange={handleChange}
                    ref={inputRef}
                    type="file"
                />
            </div>

            {state.fileName && (
                <div className="gmetrix-upload__file-row">
                    <span className="gmetrix-upload__file-name">
                        <i className="fa-regular fa-file me-2" />
                        {state.fileName}
                    </span>
                    {statusIcon}
                    <button
                        className="gmetrix-upload__clear-btn"
                        onClick={handleClear}
                        title={formatMessage({ id: 'remove.d34ac1e7', defaultMessage: 'Remove' })}
                        type="button"
                    >
                        <i className="fa-solid fa-xmark" />
                    </button>
                </div>
            )}

            {state.error && (
                <div className="gmetrix-upload__error">
                    {state.error}
                </div>
            )}
        </div>
    );

};
